import type React from 'react'
import { COLORS } from '../styles/tokens'

/* ── Helpers ─────────────────────────────────────────────────── */
export function tierBadgeStyle(tier: 'BASIC' | 'PLUS' | 'PREMIUM'): React.CSSProperties {
  if (tier === 'PREMIUM') return { background: '#7C3AED', color: '#fff' }
  if (tier === 'PLUS')    return { background: '#1D4ED8', color: '#fff' }
  return { background: '#4B5563', color: '#fff' }
}

/* ── Styles ──────────────────────────────────────────────────── */
export const styles: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh', background: COLORS.bgPage },

  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column' },

  header: {
    background: COLORS.gradientPrimary,
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  backBtn: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.8)',
    background: 'rgba(255,255,255,0.12)',
    border: 'none',
    borderRadius: 10,
    padding: '8px 14px',
    cursor: 'pointer',
    minHeight: 40,
  },
  headerTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.6rem',
    fontWeight: 700,
    color: '#FFFFFF',
    margin: 0,
  },

  content: {
    flex: 1,
    padding: '32px 24px',
    maxWidth: 900,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.5rem',
    fontWeight: 700,
    color: COLORS.greenDark,
    margin: 0,
  },
  countLabel: { fontFamily: 'Nunito, sans-serif', fontSize: '1rem', color: '#9A8070', fontWeight: 600 },

  newBtn: {
    fontSize: '0.88rem',
    fontWeight: 700,
    color: COLORS.green,
    background: '#F0FAF4',
    border: '2px solid #B7E4C7',
    borderRadius: 10,
    padding: '8px 16px',
    cursor: 'pointer',
  },
  aiBtn: {
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#5B21B6',
    background: '#F5F3FF',
    border: '2px solid #DDD6FE',
    borderRadius: 10,
    padding: '8px 16px',
    cursor: 'pointer',
  },

  createForm: {
    background: '#FFFFFF',
    border: `1.5px solid ${COLORS.borderWarm}`,
    borderRadius: 16,
    padding: '24px',
    marginBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    boxShadow: '0 2px 12px rgba(27,67,50,0.07)',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
  },
  formField: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: '0.875rem', fontWeight: 700, color: '#6B4C3B', textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: {
    fontSize: '0.95rem',
    color: '#2E1F14',
    background: COLORS.bgPage,
    border: '1.5px solid #D8C9B8',
    borderRadius: 8,
    padding: '9px 12px',
    outline: 'none',
    fontFamily: 'Nunito, sans-serif',
  },
  inputSm: {
    fontSize: '0.875rem',
    color: '#2E1F14',
    background: COLORS.bgPage,
    border: '1.5px solid #D8C9B8',
    borderRadius: 6,
    padding: '6px 8px',
    outline: 'none',
    fontFamily: 'Nunito, sans-serif',
    width: '100%',
    boxSizing: 'border-box',
  },
  submitBtn: {
    alignSelf: 'flex-start',
    fontSize: '0.95rem',
    fontWeight: 800,
    color: '#FFFFFF',
    background: COLORS.gradientPrimary,
    border: 'none',
    borderRadius: 10,
    padding: '11px 24px',
    cursor: 'pointer',
  },
  cancelBtn: {
    alignSelf: 'flex-start',
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#9A8070',
    background: 'transparent',
    border: '1.5px solid #D8C9B8',
    borderRadius: 10,
    padding: '10px 20px',
    cursor: 'pointer',
  },
  errorText: { fontSize: '0.88rem', color: '#C0392B', fontWeight: 600, margin: 0 },
  emptyText: { fontSize: '1rem', color: '#9A8070', fontWeight: 500 },

  // Themes list
  themeTable: { display: 'flex', flexDirection: 'column', gap: 10 },
  themeRow: {
    background: '#FFFFFF',
    border: `1.5px solid ${COLORS.borderWarm}`,
    borderRadius: 14,
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    boxShadow: '0 1px 6px rgba(27,67,50,0.05)',
  },
  themeInfo: { display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 },
  themeNames: { display: 'flex', alignItems: 'baseline', gap: 4 },
  themeName: { fontSize: '1rem', fontWeight: 700, color: '#2E1F14' },
  themeNameEn: { fontSize: '0.875rem', color: '#9A8070', fontWeight: 500 },
  themeDesc: { fontSize: '0.875rem', color: '#6B4C3B', margin: '2px 0 4px', fontStyle: 'italic' },
  themeMeta: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 },
  slugBadge: {
    fontSize: '0.875rem',
    background: '#F0FAF4',
    color: COLORS.green,
    border: '1px solid #B7E4C7',
    borderRadius: 6,
    padding: '2px 7px',
    fontFamily: 'monospace',
  },
  imageCount: { fontSize: '0.875rem', color: '#9A8070', fontWeight: 600 },
  themeActions: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' },
  statusBadge: {
    fontSize: '0.875rem',
    fontWeight: 800,
    borderRadius: 20,
    padding: '3px 10px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  statusPublished:  { background: '#D1FAE5', color: '#065F46' },
  statusDraft:      { background: '#FEF3C7', color: '#92400E' },
  statusGenerating: { background: '#DBEAFE', color: '#1E40AF' },
  statusFailed:     { background: '#FEE2E2', color: '#991B1B' },

  errorBox: {
    marginTop: 12,
    background: '#FFF5F5',
    border: '1.5px solid #FECACA',
    borderRadius: 10,
    padding: '14px 16px',
  },
  errorBoxHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  errorBoxTitle: {
    fontSize: '0.875rem',
    fontWeight: 800,
    color: '#991B1B',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  errorBoxClose: {
    background:     'transparent',
    border:         'none',
    cursor:         'pointer',
    color:          '#991B1B',
    padding:        '2px 6px',
    borderRadius:   4,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  errorBoxPrompt: {
    fontSize: '0.875rem',
    color: '#6B4C3B',
    margin: '0 0 8px',
  },
  errorBoxMessage: {
    fontSize: '0.875rem',
    color: '#7F1D1D',
    background: '#FEE2E2',
    border: '1px solid #FECACA',
    borderRadius: 6,
    padding: '8px 10px',
    margin: 0,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    fontFamily: 'monospace',
    maxHeight: 120,
    overflowY: 'auto' as const,
  },
  retryBtn: {
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#FFFFFF',
    background: '#991B1B',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    cursor: 'pointer',
  },
  toggleBtn: {
    fontSize: '1rem',
    background: 'rgba(27,67,50,0.07)',
    border: 'none',
    borderRadius: 8,
    width: 36,
    height: 36,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editBtn: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: COLORS.green,
    background: 'transparent',
    border: '1.5px solid #B7E4C7',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
  },
  deleteIconBtn: {
    fontSize: '1rem',
    background: 'rgba(192,57,43,0.07)',
    border: 'none',
    borderRadius: 8,
    width: 36,
    height: 36,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbSmall: {
    width: 44,
    height: 44,
    objectFit: 'contain',
    borderRadius: 8,
    flexShrink: 0,
    background: '#fff',
  },
  thumbPlaceholder: {
    width: 44,
    height: 44,
    background: COLORS.borderWarm,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    flexShrink: 0,
  },

  // Upload form extras
  filePickRow: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  filePickBtn: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: COLORS.green,
    background: '#F0FAF4',
    border: '2px dashed #B7E4C7',
    borderRadius: 10,
    padding: '12px 20px',
    cursor: 'pointer',
  },
  preview: { width: 72, height: 72, objectFit: 'contain', borderRadius: 8, border: `1.5px solid ${COLORS.borderWarm}`, background: '#fff' },

  // Image grid
  imageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 14,
  },
  imageCard: {
    background: '#FFFFFF',
    border: `1.5px solid ${COLORS.borderWarm}`,
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 1px 6px rgba(27,67,50,0.05)',
  },
  imageWrap: { position: 'relative' },
  thumbLarge: { width: '100%', aspectRatio: '1', objectFit: 'contain', display: 'block', background: '#fff' },
  coverBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    fontSize: '0.875rem',
    fontWeight: 800,
    background: '#D4A017',
    color: '#FFFFFF',
    borderRadius: 6,
    padding: '2px 7px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  imageCardBody: { padding: '8px 10px 4px' },
  imageWord: { fontSize: '0.875rem', fontWeight: 700, color: '#2E1F14', margin: 0 },
  imageDef: { fontSize: '0.875rem', color: '#9A8070', margin: '3px 0 0', lineHeight: 1.3 },
  imageCardActions: {
    padding: '6px 8px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    borderTop: '1px solid #F0EBE1',
  },
  imageActionsRow: {
    display: 'flex',
    gap: 4,
  },
  imgIconBtn: {
    flex:           1,
    background:     'transparent',
    border:         '1px solid #E8E0D4',
    borderRadius:   6,
    padding:        '5px 0',
    cursor:         'pointer',
    minWidth:       0,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  imgIconBtnAi: {
    border: '1px solid #DDD6FE',
    color: '#5B21B6',
  },
  imgIconBtnSpacer: {
    flex: 1,
    minWidth: 0,
  },
  // kept for backwards compat (upload form)
  coverBtn: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: '#9A8070',
    background: 'transparent',
    border: '1px solid #D8C9B8',
    borderRadius: 6,
    padding: '4px 8px',
    cursor: 'pointer',
  },
  deleteBtn: {
    fontSize: '0.9rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    borderRadius: 6,
  },
  regenBtn: {
    fontSize: '0.875rem',
    background: 'transparent',
    border: '1px solid #DDD6FE',
    cursor: 'pointer',
    padding: '4px 6px',
    borderRadius: 6,
    color: '#5B21B6',
  },

  // Image inline edit
  imageEditForm: {
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    borderTop: `1px solid ${COLORS.borderWarm}`,
    background: '#FDFAF6',
  },
  imageEditRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
  },
  imageEditSaveBtn: {
    flex: 1,
    fontSize: '0.875rem',
    fontWeight: 800,
    color: '#FFFFFF',
    background: COLORS.gradientPrimary,
    border: 'none',
    borderRadius: 6,
    padding: '6px 10px',
    cursor: 'pointer',
  },
  imageEditCancelBtn: {
    flex: 1,
    fontSize: '0.875rem',
    fontWeight: 700,
    color: '#9A8070',
    background: 'transparent',
    border: '1px solid #D8C9B8',
    borderRadius: 6,
    padding: '6px 10px',
    cursor: 'pointer',
  },
}
